import type { OutreachBlockReason } from './types';

/**
 * The network rules of Outreach (spec 2026-10-11 §8.5). They protect the curators and the
 * reputation of the sender. Owner request 2026-10-21: the customers send their pitches from THEIR
 * OWN mailboxes, so every rule holds inside ONE workspace (licence). Music24 does not limit a
 * curator across the whole of Music24. One exception (owner decision 2026-10-21): a hard bounce is
 * global, because the address does not exist for any workspace. An unsubscribe, a stop and a
 * complaint stay with their workspace. music24-api checks them when it schedules a message and
 * again inside the send transaction. Pure: no I/O.
 */

/** The most pitches that one contact gets in 7 days from one workspace. */
export const OUTREACH_FATIGUE_CAP_PER_WEEK = 10;
/** After a "Not a fit" reply, the same workspace waits this many days before it pitches the contact again. */
export const OUTREACH_COOL_DOWN_DAYS = 60;
/** The pitch and at most one follow-up. */
export const OUTREACH_MAX_SEQUENCE_STEPS = 2;
/**
 * The human approvals of a campaign in Review mode before "Approve the rest" may run. Autopilot
 * needs no approval (owner decision 2026-10-22): each pitch that passes P7 and the hard lint goes.
 */
export const OUTREACH_APPROVE_REST_MIN_APPROVALS = 10;
/** The most autopilot sends of one campaign in one day. */
export const OUTREACH_AUTOPILOT_DAILY_CAP = 30;
/** The weekly cap of a recipient who chose "Fewer pitches" on the stop page (design j2). */
export const OUTREACH_FEWER_PITCHES_PER_WEEK = 1;

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * The pitch preference of one recipient ("Fewer pitches" on the stop page, design j2). It is
 * keyed by the address hash. An empty genre list means "any genre".
 */
export interface OutreachRecipientPreference {
  maxPerWeek: number;
  genres: string[];
}

/**
 * The weekly cap of one recipient: the preference cap, never above the network cap and never
 * below 1. No preference: the network cap.
 */
export function recipientWeeklyCap(preference: OutreachRecipientPreference | null | undefined): number {
  if (preference === null || preference === undefined) {
    return OUTREACH_FATIGUE_CAP_PER_WEEK;
  }
  return Math.min(OUTREACH_FATIGUE_CAP_PER_WEEK, Math.max(1, Math.floor(preference.maxPerWeek)));
}

/**
 * The normal form of a genre: lowercase, "&" is "and", "-", "/" and other signs are spaces, one
 * space between words. "R&B" and "R and B" give `r and b`; "Lo-Fi" and "lo fi" give `lo fi`.
 */
export function normaliseGenre(genre: string): string {
  return genre
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Words that join two genre words and do not name a genre. */
const GENRE_JOIN_WORDS = new Set(['and']);

/** The words of a genre that count for a match: "Indie folk" gives `indie` and `folk`. */
export function genreWords(genre: string): string[] {
  return normaliseGenre(genre)
    .split(' ')
    .filter((word) => word.length >= 3 && !GENRE_JOIN_WORDS.has(word));
}

/**
 * True when a release may go to a recipient with this preference. No preference, or a preference
 * with no genre: true. Else a release genre must meet a preference genre: the same normal form
 * ("R&B" meets "r&b", "Lo-fi" meets "lo fi"), or one shared word of 3 letters or more ("indie folk"
 * meets "folk"). A short genre with no such word ("R&B") meets only by its normal form. A release
 * with no known genre does not meet a genre list: the recipient asked for these genres only. The
 * audience preview (`outreachBlockReasons`) and `sendGuard` both use this rule.
 */
export function recipientGenresMet(
  preference: OutreachRecipientPreference | null | undefined,
  releaseGenres: string[]
): boolean {
  if (preference === null || preference === undefined) {
    return true;
  }
  const wantedWholes = new Set(preference.genres.map(normaliseGenre).filter((genre) => genre !== ''));
  if (wantedWholes.size === 0) {
    return true;
  }
  const wantedWords = new Set(preference.genres.flatMap(genreWords));
  return releaseGenres.some(
    (genre) => wantedWholes.has(normaliseGenre(genre)) || genreWords(genre).some((word) => wantedWords.has(word))
  );
}

export interface OutreachBlockInput {
  /** The address is on the suppression list of this workspace, or on the global list. */
  suppressed: boolean;
  /** Pitches to this contact from this workspace in the last 7 days. */
  pitchesLast7Days: number;
  /** This workspace already pitched this release to this contact. */
  alreadyPitchedThisRelease: boolean;
  /** The last "Not a fit" reply of this contact to this workspace. */
  lastNotAFitAt: Date | null;
  now: Date;
  hasEmail: boolean;
  /** The contact genres overlap the profile genres (or the contact lists no genres). */
  genreOverlap: boolean;
  /** The user confirmed the pitch to a contact of an other genre. */
  genreConfirmed: boolean;
  /** Three or more workspaces reported that this contact asks for payment. */
  paidPlacementFlag: boolean;
  /** The "Fewer pitches" preference of the address for this workspace. Null or absent: none. */
  recipientPreference?: OutreachRecipientPreference | null;
  /** The genres of the release (the sound profile of the artist). Read only with a preference. */
  releaseGenres?: string[];
}

/** Every rule that stops a pitch to one contact now. An empty list means "the pitch can go". */
export function outreachBlockReasons(input: OutreachBlockInput): OutreachBlockReason[] {
  const reasons: OutreachBlockReason[] = [];
  if (input.suppressed) {
    reasons.push('suppressed');
  }
  if (input.pitchesLast7Days >= recipientWeeklyCap(input.recipientPreference)) {
    reasons.push('fatigue');
  }
  if (!recipientGenresMet(input.recipientPreference, input.releaseGenres ?? [])) {
    reasons.push('recipientGenres');
  }
  if (input.alreadyPitchedThisRelease) {
    reasons.push('alreadyPitchedRelease');
  }
  if (
    input.lastNotAFitAt !== null &&
    input.now.getTime() - input.lastNotAFitAt.getTime() < OUTREACH_COOL_DOWN_DAYS * DAY_MS
  ) {
    reasons.push('coolDown');
  }
  if (!input.hasEmail) {
    reasons.push('noEmail');
  }
  if (!input.genreOverlap && !input.genreConfirmed) {
    reasons.push('genreMismatch');
  }
  if (input.paidPlacementFlag) {
    reasons.push('paidPlacementFlag');
  }
  return reasons;
}

/**
 * The time when a contact at the fatigue cap can get a pitch again: the oldest of the last `cap`
 * pitches plus 7 days. Null when the contact is under the cap now. `cap` is the network cap (10),
 * or the cap of the recipient's preference (`recipientWeeklyCap`).
 */
export function fatigueFreeAgainAt(
  pitchTimesLast7Days: Date[],
  now: Date,
  cap: number = OUTREACH_FATIGUE_CAP_PER_WEEK
): Date | null {
  const inWindow = pitchTimesLast7Days
    .map((time) => time.getTime())
    .filter((time) => time > now.getTime() - WEEK_MS && time <= now.getTime())
    .sort((a, b) => b - a);
  const limit = Math.max(1, cap);
  if (inWindow.length < limit) {
    return null;
  }
  const oldestOfLastCap = inWindow[limit - 1];
  if (oldestOfLastCap === undefined) {
    return null;
  }
  return new Date(oldestOfLastCap + WEEK_MS);
}
